import React, {useEffect} from 'react';
import {Flex, Button, Checkbox, Form, Submit, List, Display, OpenAi} from 'components';
import {flow, Modal, useQuery, UseQueryResult, useSelector, openai} from 'helpers';
import {calendar, session, settings} from 'store';
import {Goal} from 'types';

interface Input {
  content?: string;
  minimal?: boolean;
  name: string;
  onSuccess: (values: {goal: Partial<Goal>}) => void;
}

export const SmartGoalModal: Modal<Input> = (props) => {
  const {name, popup} = props;
  const {today} = useSelector(calendar.days);

  const query = useQuery({
    enabled: false,
    throwOnError: true,
    queryKey: ['openai', 'goal', 'smart', name],
    staleTime: Infinity,
    queryFn: () => openai.chat.completions(`
      Act as a Goal Setting expert.

      Convert this goal into SMART (Specific, Measurable, Achievable, Relevant, Time-bound) goal: "${name}"

      - Generate 5 different variations of the SMART goal
      - Each variation should have a different target date and/or target value (if measurable).
      - Include the Target Date and Target Value in the title (if applicable).
      - Within the Title, format the Target Date as "MMM d, yyyy".
      - Use "MM/dd/yyyy" format for the "target_date" field.
      - If the goal is not measurable, leave "target_value" empty.
      - If measurable, "target_value" must be numeric and is typically achieved over time.
      - Define "unit" based on the type of goal:
        - Use "$" for financial goals.
        - Use "kg", "lbs" for weight-related goals.
        - Use "hours" for time-related goals.
        - Use a custom label (e.g., "clients", "products") if applicable.
      - The starting date is ${today.format()}.

      The response must be in JSON format, structured as follows:
      [
        {
          "name": "",
          "target_date": ${today.unix()},
          "target_value": "",
          "note": "description",
          "unit": ""
        }
      ]
      where "target_date" is in unix timestamp format
    `, 1)
  });

  useEffect(() => popup.bounce(), [query.data]);

  return (
    <>
      <h4>Goal Setup</h4>
      <Display {...props} what={query.data ? GoalList : Dialog} query={query} />
    </>
  );
};

const Dialog: Modal<Input & {query: UseQueryResult}> = ({query, popup, content, minimal}) => {
  const {gptConnects} = useSelector(settings.settings);
  const {free} = useSelector(session.subscription);
  return (
    <>
      <p className="lead">
        {content ?? 'Would you like to use ChatGPT to suggest a SMART goal for you, and then create a detailed Action Plan for it?'}
      </p>
      <p className="small-spacing">
        <OpenAi.Alert />
      </p>
      <Flex className={['spacing', 'right']}>
        {gptConnects > 0 ? (
          <>
            <Button
              onClick={popup.close}
              className="subtle"
              children={minimal ? 'Cancel' : 'No, I will create it myself'}
              disabled={query.isFetching}
            />
            <Button
              icon="check"
              query={query}
              className="primary"
              children="Yes, ask ChatGPT!"
            />
          </>
        ) : (
          <>
            <Button
              onClick={popup.close}
              className="secondary"
              children="Close"
            />
            {free && (
              <Button
                className="orange"
                to="/settings/membership/"
                onClick={popup.close}
                children="Upgrade Account"
              />
            )}
          </>
        )}
      </Flex>
    </>
  );
};

const GoalList: Modal<Input & {query: UseQueryResult}> = ({query, popup, minimal, onSuccess}) => (
  <>
   <p>Select one of the suggested goals:</p>
   <Form className="spacing" onSubmit={flow(onSuccess, popup.close)}>
     {({values, setValues, isSubmitting}) => (
       <List className="small-gutter">
         {query.data[0]?.map((goal, index) =>
           <Checkbox.Container radio white onClick={() => setValues({goal})} checked={values.goal === goal} key={index}>
             <div>
              <b>{goal.name}</b><br />
              <small className="faded">{goal.note}</small>
             </div>
           </Checkbox.Container>
         )}
         <Flex className={['right', 'smedium-spacing']}>
           <Button
             onClick={popup.close}
             className="subtle"
             children={minimal ? 'Cancel' : 'No, I will create it myself'}
             disabled={isSubmitting}
           />
           <Submit disabled={!values.goal}>
             {['Create', 'Creating..']}
           </Submit>
         </Flex>
       </List>
     )}
   </Form>
  </>
);



